/* The Guide Foundry - motion layer.
   Progressive enhancement only: if this file never runs, every element stays
   visible. Everything is transform/opacity, rAF-batched, and the whole file
   bails out under prefers-reduced-motion. */
(function () {
  'use strict';

  var root = document.documentElement;
  var motionOK =
    !window.matchMedia('(prefers-reduced-motion: reduce)').matches &&
    'IntersectionObserver' in window;

  if (!motionOK) return;
  root.classList.add('tgf-motion');

  /* ---------------------------------------------------------------- reveal */

  /* Staggered: siblings in a grid come in one after the other. */
  var GROUPS = [
    '.tgf-cats__grid > li',
    '.product-grid > li',
    '.multicolumn-list > li',
    '.tgf-trust__item',
    '.collapsible-content .accordion',
    '.tgf-prob__grid > li',
    '.tgf-value__item',
    '.tgf-ach__card',
    '.tgf-chapter',
    '.tgf-part',
    '.tgf-offer__steps li',
    '.tgf-note__links li',
    '.tgf-facts li',
    '.tgf-learn li',
    '.tgf-head__meta li'
  ];

  /* Single blocks: copy, headings, media. */
  var BLOCKS = [
    '.tgf-cats__head',
    '.title-wrapper',
    '.collapsible-content__header',
    '.newsletter__wrapper',
    '.tgf-hero__copy',
    '.tgf-hero__shelf',
    '.tgf-prob__head',
    '.tgf-value__head',
    '.tgf-value__foot',
    '.tgf-sec__head',
    '.tgf-note__copy',
    '.tgf-offer__copy',
    '.tgf-problem__media',
    '.tgf-problem__text',
    '.tgf-fit > div'
  ];

  /* A few things read better arriving from the side than from below. */
  var VARIANTS = [
    ['.tgf-ba__col--before', 'tgf-reveal--left'],
    ['.tgf-ba__col--after', 'tgf-reveal--right'],
    ['.tgf-problem__media', 'tgf-reveal--scale'],
    ['.tgf-hero__shelf', 'tgf-reveal--scale'],
    ['.tgf-head__meta li', 'tgf-reveal--fade'],
    ['.tgf-facts li', 'tgf-reveal--fade']
  ];

  var io = new IntersectionObserver(
    function (entries) {
      for (var i = 0; i < entries.length; i++) {
        if (!entries[i].isIntersecting) continue;
        entries[i].target.classList.add('is-in');
        io.unobserve(entries[i].target);
      }
    },
    { rootMargin: '0px 0px -8% 0px', threshold: 0.05 }
  );

  var fold = window.innerHeight * 0.94;

  function watch(el, index) {
    if (!el || el.classList.contains('tgf-reveal')) return;
    /* Anything already on screen at load renders normally: never delay the
       first paint, and never animate what the visitor is already reading. */
    if (el.getBoundingClientRect().top < fold) return;
    el.classList.add('tgf-reveal');
    if (index) el.style.setProperty('--tgf-i', index > 6 ? 6 : index);
    io.observe(el);
  }

  function scan(scope) {
    var ctx = scope || document;

    GROUPS.forEach(function (sel) {
      var nodes = ctx.querySelectorAll(sel);
      var last = null;
      var n = 0;
      for (var i = 0; i < nodes.length; i++) {
        if (nodes[i].parentNode !== last) { last = nodes[i].parentNode; n = 0; }
        watch(nodes[i], n++);
      }
    });

    BLOCKS.forEach(function (sel) {
      var nodes = ctx.querySelectorAll(sel);
      for (var i = 0; i < nodes.length; i++) watch(nodes[i], 0);
    });

    VARIANTS.forEach(function (pair) {
      var nodes = ctx.querySelectorAll(pair[0]);
      for (var i = 0; i < nodes.length; i++) {
        if (nodes[i].classList.contains('tgf-reveal')) nodes[i].classList.add(pair[1]);
      }
    });

    /* The before/after columns are blocks, but only once they have a variant. */
    var ba = ctx.querySelectorAll('.tgf-ba__col');
    for (var j = 0; j < ba.length; j++) watch(ba[j], 0);
    VARIANTS.forEach(function (pair) {
      var nodes = ctx.querySelectorAll(pair[0]);
      for (var k = 0; k < nodes.length; k++) {
        if (nodes[k].classList.contains('tgf-reveal')) nodes[k].classList.add(pair[1]);
      }
    });
  }

  scan(document);

  /* Sections re-rendered by the theme editor come back unobserved. */
  document.addEventListener('shopify:section:load', function (e) { scan(e.target); });

  /* ------------------------------------------------- header lifts on scroll */

  var scrolled = false;
  var headerTick = false;

  function headerPaint() {
    headerTick = false;
    var past = window.scrollY > 40;
    if (past === scrolled) return;
    scrolled = past;
    root.classList.toggle('tgf-scrolled', past);
  }

  window.addEventListener(
    'scroll',
    function () {
      if (headerTick) return;
      headerTick = true;
      requestAnimationFrame(headerPaint);
    },
    { passive: true }
  );
  headerPaint();

  /* ------------------------------------------------------- pointer effects */

  var fine = window.matchMedia('(hover: hover) and (pointer: fine)').matches;

  if (fine) {
    var glowPending = false;
    var glowQueue = [];

    document.addEventListener(
      'pointermove',
      function (e) {
        var el = e.target.closest ? e.target.closest('[data-tgf-glow]') : null;
        if (!el) return;
        glowQueue.push([el, e.clientX, e.clientY]);
        if (glowPending) return;
        glowPending = true;
        requestAnimationFrame(function () {
          glowPending = false;
          var job = glowQueue.pop();
          glowQueue.length = 0;
          if (!job) return;
          var box = job[0].getBoundingClientRect();
          job[0].style.setProperty('--mx', (((job[1] - box.left) / box.width) * 100).toFixed(1) + '%');
          job[0].style.setProperty('--my', (((job[2] - box.top) / box.height) * 100).toFixed(1) + '%');
        });
      },
      { passive: true }
    );
  }

  /* ------------------------------------------------------------- parallax */

  var layers = [].slice.call(document.querySelectorAll('[data-tgf-parallax]'));

  if (fine && layers.length && window.innerWidth >= 990) {
    var ticking = false;

    var frame = function () {
      ticking = false;
      var mid = window.innerHeight / 2;
      for (var i = 0; i < layers.length; i++) {
        var el = layers[i];
        var box = el.getBoundingClientRect();
        if (box.bottom < -200 || box.top > window.innerHeight + 200) continue;
        var factor = parseFloat(el.getAttribute('data-tgf-parallax')) || 0.05;
        var shift = (box.top + box.height / 2 - mid) * factor;
        if (shift > 60) shift = 60;
        if (shift < -60) shift = -60;
        el.style.transform = 'translate3d(0,' + shift.toFixed(1) + 'px,0)';
      }
    };

    var onScroll = function () {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(frame);
    };

    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll, { passive: true });
    frame();
  }
})();
